import { titleCase } from '../titleCase.ts';
import { holdersOf, type ProjectProcess } from './graph.ts';
import { TASK_PHASE_RULE, earlierPhase, phaseIndex, phasesOf, type PhaseKey } from './phases.ts';
import type { ProcessTreeDoc } from './schema.ts';
import { projectPhases } from './standing.ts';

/**
 * Work a later step needs done early (design study 31, slice 4; Jason, 2026-10-29: "a very simple way to add tasks to a
 * node in each of other phases"): a step's task done in an earlier phase than its step, set per task on the master (its
 * own phase, never a later one than its step's: `TASK_PHASE_RULE`) or on the job. Its step's holds do not hold it, the
 * gate opening its own phase does, and it holds the gate closing the phase it is done in (Settled 1), as every step of
 * that phase does; its step is not done until it is, as with any task. The engine reads it from the job's phases (core
 * `projectPhases`, `earlierPhase`): in the order of work a task done in an earlier phase than the step holding it is
 * not after that step's start, only before its end.
 */
export interface EarlyTask {
  /** The step at the top it is a task of. */
  stepId: string;
  /** The phase it is done in. */
  phase: PhaseKey;
}

/** Each live task of a job done in an earlier phase than the step at its top, by its id. */
export function earlyTasks(doc: ProcessTreeDoc, p: ProjectProcess): Map<string, EarlyTask> {
  const phases = projectPhases(doc, p, null);
  const live = new Set(p.steps.filter((s) => !s.off).map((s) => s.id));
  const out = new Map<string, EarlyTask>();
  for (const s of p.steps) {
    if (s.off || s.parentId === null) continue;
    const top = holdersOf(p, s.id).at(-1);
    if (top === undefined || !live.has(top) || !earlierPhase(phases, s.id, top)) continue;
    out.set(s.id, { stepId: top, phase: phases.get(s.id)! });
  }
  return out;
}

/**
 * A task of a job set to be done in `phase`, in the list of `holderId` (a task added there, or one already in it): only
 * its step's phase or an earlier one, its step the one at the top, in the phase the job reads that step in (`projectPhases`).
 * The refusal in words (`TASK_PHASE_RULE`), or null where it may be.
 */
export function taskPhaseRefused(doc: ProcessTreeDoc, p: ProjectProcess, holderId: string, title: string, phase: PhaseKey): string | null {
  const topId = holdersOf(p, holderId).at(-1) ?? holderId;
  const top = p.steps.find((s) => s.id === topId);
  const own = projectPhases(doc, p, null).get(topId);
  if (!top || own === undefined || phaseIndex(phase) <= phaseIndex(own)) return null;
  const name = (k: PhaseKey) => phasesOf(doc).find((x) => x.key === k)?.name ?? k;
  return `${titleCase(title)} cannot be done in ${name(phase)}: ${titleCase(top.title)} is in ${name(own)}. ${TASK_PHASE_RULE}`;
}
